/**
 * Push notifications bridge.
 *
 * - Requests permission on login (first run only).
 * - Registers the device with APNs / FCM and POSTs the token to the
 *   backend at `/me/device-tokens` so scheduled jobs can push status-change
 *   and new-message alerts.
 * - Listens for incoming push while the app is foregrounded and surfaces a
 *   sonner toast; background pushes are handled natively by the OS.
 */
import { PushNotifications } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { isNative, platform } from "./platform";

const DEVICE_TOKEN_KEY = "cs_pushToken_v1";

export async function registerPush() {
  if (!isNative()) return;
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") {
    logger.debug("[push] permission denied or prompt deferred");
    return;
  }
  await PushNotifications.register();
}

export async function attachPushHandlers(onForegroundToast) {
  if (!isNative()) return;
  await PushNotifications.addListener("registration", async (token) => {
    try {
      await api.post("/me/device-tokens", {
        token: token.value,
        platform: platform(),
      });
      await Preferences.set({ key: DEVICE_TOKEN_KEY, value: token.value });
    } catch (err) {
      logger.debug("[push] token POST failed:", err);
    }
  });
  await PushNotifications.addListener("registrationError", (err) => {
    logger.debug("[push] registrationError:", err);
  });
  await PushNotifications.addListener("pushNotificationReceived", (notif) => {
    onForegroundToast(
      notif.title || "New update",
      notif.body || "Tap to open.",
    );
  });
}
